import { ArrowUpRight } from "lucide-react";
import { Reveal, Magnetic } from "./Shared";

const IMG = {
  living1: "https://images.unsplash.com/photo-1630699293259-0b6c08606c62?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  living2: "https://images.unsplash.com/photo-1541194577687-8c63bf9e7ee3?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  lounge: "https://images.unsplash.com/photo-1672853783321-73c5aa3be6b8?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
  interior: "https://images.unsplash.com/photo-1560448075-57d0285fc59b?q=80&w=1600&auto=format&fit=crop&ixlib=rb-4.1.0",
};

const mandates = [
  {
    n: "01",
    img: IMG.living1,
    name: "The Beaumont",
    client: "Owner · Dubai",
    place: "Soho, London",
    desc: "A yield blueprint and premium-let repositioning for a compact one-bedroom Soho retreat — rents remodelled to micro-market data, layout re-planned, operator independently selected.",
    specs: ["1 BR", "Advisory Blueprint", "2023"],
  },
  {
    n: "02",
    img: IMG.living2,
    name: "The Hawthorne",
    client: "Family Office · Singapore",
    place: "Covent Garden, London",
    desc: "Full refurbishment oversight on a two-bedroom townhouse apartment — contractors vetted, milestones audited, every invoice verified before hand-back.",
    specs: ["2 BR", "Refurb Oversight", "2024"],
  },
  {
    n: "03",
    img: IMG.lounge,
    name: "The Wellington",
    client: "Investor · Hong Kong",
    place: "Westminster, London",
    desc: "A full written mandate for an air-conditioned three-bedroom by Trafalgar Square — advisory blueprint, capital works oversight, and PropTech onboarding.",
    specs: ["3 BR", "Full Mandate", "2024"],
  },
  {
    n: "04",
    img: IMG.interior,
    name: "The Langford",
    client: "Owner · Zurich",
    place: "Marylebone, London",
    desc: "A retained owner-side engagement over a mansion-block residence — operator selection, monthly remote audits, quarterly yield reviews.",
    specs: ["2 BR", "Retainer", "Ongoing"],
  },
];

export default function Portfolio() {
  return (
    <section id="mandates" data-testid="portfolio-section" className="border-b border-black/15 bg-[#EBE9E1]">
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 py-24 md:py-40">
        <div className="text-center max-w-3xl mx-auto mb-20 md:mb-28">
          <Reveal y={20} className="flex items-center justify-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-[#D4512D]">
            <span className="inline-block h-px w-12 bg-[#D4512D]" aria-hidden="true" />
            <span data-testid="portfolio-tag">Portfolio &amp; case studies</span>
            <span className="inline-block h-px w-12 bg-[#D4512D]" aria-hidden="true" />
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-10 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              A selection of <em className="font-light">assets we advise on.</em>
            </h2>
            <p className="mt-8 text-base lg:text-lg leading-relaxed text-black/60 font-light">
              From a compact Soho retreat to an air-conditioned residence by Trafalgar Square — a curated look at
              prime London assets owned by global clients and advised under written mandate.
            </p>
            <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4512D]" data-testid="portfolio-disclaimer">
              We advise the owners of these assets — we never sell, let, or manage the units ourselves.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-8 md:gap-12">
          {mandates.map((m, i) => (
            <Reveal key={m.n} delay={(i % 2) * 0.12} className="h-full">
              <article
                data-testid={`mandate-card-${m.n}`}
                className="group h-full bg-[#F4F3EF] border border-black/15 flex flex-col"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={m.img}
                    alt={`${m.name} — advised asset in ${m.place}`}
                    data-testid={`mandate-image-${m.n}`}
                    className="w-full aspect-[4/3] object-cover grayscale group-hover:grayscale-0 group-hover:scale-[1.04] transition-[filter,transform] duration-700 will-change-transform"
                  />
                  <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-[#0A0A0A]/70 text-[#F4F3EF] backdrop-blur px-3 py-1.5">
                    {m.place}
                  </span>
                </div>
                <div className="p-8 md:p-10 flex flex-col flex-1">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D4512D]">
                    {m.client} · Case Study {m.n}
                  </p>
                  <h3 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight group-hover:translate-x-2 transition-transform duration-500 will-change-transform">
                    {m.name}
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-black/55 font-light flex-1">{m.desc}</p>
                  <div className="mt-8 flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.2em] text-black/50">
                    {m.specs.map((s, si) => (
                      <span key={s} className="flex items-center gap-4">
                        {si > 0 && <span className="text-[#D4512D]" aria-hidden="true">·</span>}
                        {s}
                      </span>
                    ))}
                  </div>
                  <a
                    href="#contact"
                    data-testid={`mandate-link-${m.n}`}
                    className="mt-6 inline-flex items-center gap-2 link-sweep font-mono text-[11px] uppercase tracking-[0.25em] text-black/70 hover:text-black transition-colors duration-300 self-start"
                  >
                    View Case Study <ArrowUpRight size={14} strokeWidth={1.5} />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 md:mt-20 flex justify-center">
          <Magnetic>
            <a
              href="#contact"
              data-testid="portfolio-cta-button"
              className="inline-flex items-center gap-3 rounded-full border border-black/25 px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-black/80 transition-colors duration-300 hover:bg-[#0A0A0A] hover:text-[#F4F3EF] hover:border-[#0A0A0A]"
            >
              Discuss Your Portfolio <ArrowUpRight size={15} strokeWidth={1.5} />
            </a>
          </Magnetic>
        </Reveal>
      </div>
    </section>
  );
}
